import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createCollectionItem,
  deleteDocumentAndSubcollection,
  saveCollectionItem,
  subscribeToCollection,
} from "./utils";

export default function useMoodboards({ admin, user }) {
  const [moodboards, setMoodboards] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  let unsubscribeRef = useRef();
  const userId = user?.uid;

  useEffect(() => {
    unsubscribeRef.current && unsubscribeRef.current();
    subscribeToCollection(
      "moodboards",
      admin,
      userId,
      ({ items, meta, error, unsubscribe }) => {
        if (items) {
          setMoodboards(items);
        }
        if (meta) {
          setMeta(meta);
        }
        if (error) {
          setError(error);
        }
        if (unsubscribe) {
          unsubscribeRef.current = unsubscribe;
        }
      }
    );

    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, [admin, userId]);

  const moodboardPathAvailable = useCallback(
    (path, moodboardId) => {
      let moodboardIds = Object.keys(moodboards);
      for (let i in moodboardIds) {
        let currentId = moodboardIds[i];
        let current = moodboards[currentId];
        if (current.path) {
          if (current.path.toLowerCase() === path.toLowerCase()) {
            if (moodboardId && moodboardId !== currentId) return false;
          }
        }
      }
      return true;
    },
    [moodboards]
  );

  const addMoodboard = useCallback(
    (data, callback) => {
      if (data.name && data.path) {
        if (moodboardPathAvailable(data.path)) {
          createCollectionItem("moodboards", data, callback);
        } else {
          callback({ error: "path in use" });
        }
      } else {
        callback({ error: "Name and path required" });
      }
    },
    [moodboardPathAvailable]
  );

  const saveMoodboardInfo = useCallback(
    (moodboardId, data, callback) => {
      if (data.name && data.path) {
        if (moodboardPathAvailable(data.path, moodboardId)) {
          // Add createdBy field if not present (for existing moodboards)
          if (!data.createdBy && userId) {
            data.createdBy = userId;
          }
          saveCollectionItem("moodboards", moodboardId, data, callback);
        } else {
          callback({ error: "path in use" });
        }
      } else {
        callback({ error: "Name and path required" });
      }
    },
    [moodboardPathAvailable, userId]
  );

  const deleteMoodboard = useCallback((moodboardId, callback) => {
    deleteDocumentAndSubcollection(
      "moodboards",
      moodboardId,
      "items",
      callback
    );
  }, []);

  // All signed-in users can add/edit/delete moodboards
  const userFunctions = useMemo(
    () => ({ addMoodboard, saveMoodboardInfo, deleteMoodboard }),
    [addMoodboard, saveMoodboardInfo, deleteMoodboard]
  );

  return { moodboards, adminFunctions: userFunctions, meta, error };
}
